/** Consumer planning enforces the versioned band policy. Historical replay is explicit. */
import type { Cadence, Recording, SessionPlan } from '../types.ts';
import { composeSession, type CompositionResult } from './composition.ts';
import { planFingerprint as legacyFingerprint, type PlanFingerprint as LegacyFingerprint,
  type PlanOptions, type SessionReach } from './legacy-plan.ts';
export {
  comingOutIsland, cueTrimFor, distribute, effectiveGaps, InvalidEditError, normalizeEdits,
  planDiagnostics, protectedTransitions, selectIslands, StructuralPlanError, UnreachableError,
  planSession as legacyPlanSession,
} from './legacy-plan.ts';
export type { PlanOptions, SessionReach, PlanDiagnostics } from './legacy-plan.ts';
export type PlanFingerprint = LegacyFingerprint & { version?: 2; policyVersion?: string;
  variants?: Array<{id:string;decisionId:string}>; wordlessIslands?: number[];
  speechFrames?: Array<[number,number,number]>; gapFrames?: number[] };
export function planFingerprint(plan:SessionPlan,recording:Recording):PlanFingerprint {
  const base=legacyFingerprint(plan,recording);
  return plan.composition?{...base,version:2,policyVersion:plan.composition.policyVersion,
    variants:plan.composition.variants,wordlessIslands:plan.composition.wordlessIslands,
    speechFrames:plan.timeline.flatMap(s=>s.kind==='speech'?[[s.island,Math.round((s.srcStart??recording.speech[s.island].srcStart)*48000),Math.round((s.srcEnd??recording.speech[s.island].srcEnd)*48000)] as [number,number,number]]:[]),
    gapFrames:plan.timeline.filter(s=>s.kind==='tone').map(s=>Math.round((s.endSec-s.startSec)*48000))}:base;
}
export class CompositionUnavailableError extends Error {
  readonly result: Extract<CompositionResult,{status:'unavailable'}>;
  constructor(result:Extract<CompositionResult,{status:'unavailable'}>){super(result.detail);this.name='CompositionUnavailableError';this.result=result;}
}
export function planSession(rec:Recording,targetSec:number,cadence:Cadence,opts:PlanOptions={}):SessionPlan {
  const result=composeSession(rec,targetSec,cadence,opts);
  if(result.status!=='conforming')throw new CompositionUnavailableError(result);
  return result.plan;
}
export function sessionReach(rec:Recording,targetSec:number,cadence:Cadence,opts:PlanOptions={}):SessionReach {
  const result=composeSession(rec,targetSec,cadence,opts),intervals=result.feasibleTargets;
  if(!intervals.length){if(result.status==='unavailable')throw new CompositionUnavailableError(result);throw Error('No target intervals');}
  return {minSec:Math.min(...intervals.map(i=>i.minSec)),maxSec:Math.max(...intervals.map(i=>i.maxSec))};
}
/** Reach is a projection of the exact intervals, never their loose envelope. */
export function canReach(rec:Recording,targetSec:number,cadence:Cadence,toleranceSec=30,opts:Pick<PlanOptions,'desiredSpeechRatio'>={}):boolean {
  return composeSession(rec,targetSec,cadence,{...opts,toleranceSec}).status==='conforming';
}
